import React from 'react'
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { poolwerxColors } from '../../../../theme/colors'

export interface SegmentedOption<T extends string | number> {
  label: string
  value: T
  /** Optional leading icon (e.g. a brand mark) rendered before the label. */
  icon?: React.ReactNode
}

export interface SegmentedSelectorProps<T extends string | number> {
  options: ReadonlyArray<SegmentedOption<T>>
  value: T
  onChange: (value: T) => void
  size?: 'small' | 'medium' | 'large'
  disabled?: boolean
  ariaLabel?: string
}

/**
 * Single-select segmented control (a thin wrapper over MUI
 * `ToggleButtonGroup`/`ToggleButton`). Exclusive selection; ignores the
 * deselect-to-null click so a value is always chosen. Reusable across admin
 * forms - see docs/development/admin-forms.md.
 */
function SegmentedSelector<T extends string | number>({
  options,
  value,
  onChange,
  size = 'small',
  disabled = false,
  ariaLabel,
}: SegmentedSelectorProps<T>) {
  const handleChange = (_event: React.MouseEvent<HTMLElement>, next: T | null) => {
    if (next !== null) onChange(next)
  }

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      size={size}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {options.map((opt) => (
        <ToggleButton
          key={String(opt.value)}
          value={opt.value}
          sx={{
            textTransform: 'none',
            px: 2,
            fontWeight: 500,
            '&.Mui-selected': {
              bgcolor: poolwerxColors.teal,
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: poolwerxColors.tealHover },
            },
          }}
        >
          {opt.icon ? (
            <Box
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
            >
              {opt.icon}
              {opt.label}
            </Box>
          ) : (
            opt.label
          )}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

export default SegmentedSelector
